import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";

function formatDateYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isWorkingDOTW(user, day) {
  let working = false;
  switch (day) {
    case 1:
      working = (user?.UserShiftMask & 0b00000001) !== 0;
      break;
    case 2:
      working = (user?.UserShiftMask & 0b00000010) !== 0;
      break;
    case 3:
      working = (user?.UserShiftMask & 0b00000100) !== 0;
      break;
    case 4:
      working = (user?.UserShiftMask & 0b00001000) !== 0;
      break;
    case 5:
      working = (user?.UserShiftMask & 0b00010000) !== 0;
      break;
    case 6:
      working = (user?.UserShiftMask & 0b00100000) !== 0;
      break;
    case 0:
      working = (user?.UserShiftMask & 0b01000000) !== 0;
      break;
    default:
      break;
  }
  return working;
}

function getMonthDates(startDate) {
  const start = new Date(startDate);
  start.setHours(12, 0, 0, 0);
  const startOfMonth = new Date(start.setDate(1));
  const daysInMonth = new Date(
    start.getFullYear(),
    (start.getMonth() + 1) % 12,
    0,
  ).getDate();
  const dates = [];
  for (let i = 0; i < daysInMonth; i++) {
    const d = new Date(startOfMonth);
    d.setDate(startOfMonth.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function getWeekDates(startDate) {
  const start = new Date(startDate);
  start.setHours(12, 0, 0, 0);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(start.setDate(diff));
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function getWeekHours(user, default_hours) {
  const hours = [];
  for (let i = 0; i < 7; i++) {
    hours.push(isWorkingDOTW(user, (i + 1) % 7) ? default_hours : 0);
  }
  return hours;
}

const styles = {
  burgerButton: {
    padding: 6,
    borderRadius: 4,
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "120%",
    background: "white",
    border: "1px solid #eee",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    borderRadius: 6,
    zIndex: 40,
    padding: 8,
  },
  menuItem: {
    background: "none",
    border: "none",
    padding: "8px 12px",
    textAlign: "left",
    cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    background: "white",
    padding: 16,
    borderRadius: 8,
    minWidth: 160,
    minHeight: 80,
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
  },
  panel: {
    background: "white",
    padding: 12,
    borderRadius: 8,
    border: "1px solid #eee",
    marginTop: 12,
  },
};

function RecordEditOverlay({ record, onChange, onCancel }) {
  const cancelRef = useRef(null);
  const doChange = useCallback(
    function () {
      onChange(record);
      onCancel();
    },
    [onChange, onCancel, record],
  );

  useEffect(() => {
    if (cancelRef.current) cancelRef.current.focus();
  }, []);

  if (record === null) return;

  return (
    <div style={styles.modalBackdrop} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: 0, textAlign: "center" }}>Delete Record</h2>
        <h4 style={{ margin: 10, textAlign: "center" }}>
          <i>Are you sure?</i>
        </h4>
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <button
            onClick={onCancel}
            className="vivid-button"
            autoFocus
            ref={cancelRef}
            style={{ "--bg": "darkred" }}
          >
            Cancel
          </button>
          <button
            onClick={doChange}
            className="vivid-button"
            style={{ "--bg": "green" }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordUpdateOverlay({ record, onChange, onCancel }) {
  const [hours, setHours] = useState(0);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!record) return;
    setHours(Number(record.TimesheetHours) || 0);
    setNotes(record.TimesheetNotes || "");
  }, [record]);

  if (record === null) return;

  return (
    <div style={styles.modalBackdrop} onClick={onCancel}>
      <div
        style={{ ...styles.modal, minWidth: 360 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: 0, textAlign: "center" }}>Edit Record</h2>
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            Hours
            <input
              type="number"
              min={0}
              max={24}
              step={0.25}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            Notes
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={255}
            />
          </label>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            marginTop: 12,
          }}
        >
          <button
            onClick={onCancel}
            className="vivid-button"
            style={{ "--bg": "darkred" }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onChange({
                ...record,
                TimesheetHours: Number(hours) || 0,
                TimesheetNotes: notes,
              });
              onCancel();
            }}
            className="vivid-button"
            style={{ "--bg": "green" }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TimesheetsPage({ user, initialDate = null }) {
  const initDate = initialDate ? new Date(initialDate) : null;
  const normalizedRole = String(user?.UserRole || "")
    .trim()
    .toUpperCase();
  const canManageOtherUsers = normalizedRole === "SENIOR ADMIN";
  const [mode, setMode] = useState(initDate ? "week" : "day");
  const [date, setDate] = useState(initDate || new Date());
  const [month, setMonth] = useState(initDate || new Date());
  const [hours, setHours] = useState(user?.UserDailyHours || 7.5);
  const [weekHours, setWeekHours] = useState(getWeekHours(user, hours));

  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [status, setStatus] = useState("");
  const [weekRecords, setWeekRecords] = useState([]);
  const [monthRecords, setMonthRecords] = useState([]);
  const [loadingWeekRecords, setLoadingWeekRecords] = useState(false);
  const [editModal, setEditModal] = useState({ record: null });
  const [updateModal, setUpdateModal] = useState({ record: null });
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user?.UserID || null);

  const selectedTimesheetUser = canManageOtherUsers
    ? users.find((u) => Number(u.UserID) === Number(selectedUserId)) || user
    : user;
  const targetUserIdForApi =
    canManageOtherUsers && Number.isFinite(Number(selectedUserId))
      ? Number(selectedUserId)
      : null;

  useEffect(() => {
    setWeekHours(getWeekHours(selectedTimesheetUser, hours));
  }, [selectedTimesheetUser, hours, mode]);

  useEffect(() => {
    setSelectedUserId(user?.UserID || null);
  }, [user]);

  useEffect(() => {
    if (!canManageOtherUsers) {
      setUsers([]);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const data = await apiFetch("/api/users");
        const list =
          data && Array.isArray(data.users) ? data.users : user ? [user] : [];
        if (mounted) {
          setUsers(list);
          const hasCurrent = list.some(
            (u) => Number(u.UserID) === Number(selectedUserId),
          );
          if (!hasCurrent && list.length > 0) {
            setSelectedUserId(list[0].UserID);
          }
        }
      } catch (e) {
        console.warn("Could not load users", e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [canManageOtherUsers, user, selectedUserId]);

  useEffect(() => {
    setHours(selectedTimesheetUser?.UserDailyHours || 7.5);
    setWeekHours(
      getWeekHours(
        selectedTimesheetUser,
        selectedTimesheetUser?.UserDailyHours || 7.5,
      ),
    );
  }, [selectedTimesheetUser]);

  useEffect(() => {
    setDate(month);
  }, [month]);

  useEffect(() => {
    // If the parent passes a different initialDate after mount, update local state
    if (initialDate) {
      const d = new Date(initialDate);
      setDate(d);
      setMonth(d);
      setMode("week");
    }

    let mounted = true;
    (async () => {
      try {
        const p = await apiFetch("/api/projects");
        const plist = p && Array.isArray(p.projects) ? p.projects : [];
        if (mounted) setProjects(plist);
      } catch (e) {
        console.warn("Could not load projects", e);
      }
      try {
        const a = await apiFetch("/api/activities");
        const alist = a && Array.isArray(a.activities) ? a.activities : [];
        if (mounted) setActivities(alist);
      } catch (e) {
        console.warn("Could not load activities", e);
      }
    })();
    return () => (mounted = false);
  }, [initialDate]);

  // fetch records for the currently-selected week so we can render the breakdown
  useEffect(() => {
    let mounted = true;
    const fetchWeek = async () => {
      if (mode === "month") return;
      if (status !== "Saved" && status !== "") return;
      if (!selectedTimesheetUser) return;
      setLoadingWeekRecords(true);
      try {
        const dates = mode === "day" ? [date] : getWeekDates(date);
        const start = formatDateYMD(dates[0]);
        const end = formatDateYMD(dates[dates.length - 1]);
        const data = await apiFetch(
          targetUserIdForApi !== null
            ? `/api/timesheets/me?StartDate=${start}&EndDate=${end}&UserID=${targetUserIdForApi}`
            : `/api/timesheets/me?StartDate=${start}&EndDate=${end}`,
        );
        if (mounted) setWeekRecords(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("Could not fetch week records", e);
        if (mounted) setWeekRecords([]);
      } finally {
        if (mounted) setLoadingWeekRecords(false);
      }
    };

    fetchWeek();

    return () => (mounted = false);
  }, [selectedTimesheetUser, date, status, mode, targetUserIdForApi]);

  useEffect(() => {
    let mounted = true;
    const fetchMonth = async () => {
      if (mode !== "month") return;
      if (status !== "Saved" && status !== "") return;
      if (!selectedTimesheetUser) return;
      setLoadingWeekRecords(true);
      try {
        const dates = getMonthDates(date);
        const start = formatDateYMD(dates[0]);
        const end = formatDateYMD(dates[dates.length - 1]);
        const data = await apiFetch(
          targetUserIdForApi !== null
            ? `/api/timesheets/me?StartDate=${start}&EndDate=${end}&UserID=${targetUserIdForApi}`
            : `/api/timesheets/me?StartDate=${start}&EndDate=${end}`,
        );
        if (mounted) setMonthRecords(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("Could not fetch week records", e);
        if (mounted) setMonthRecords([]);
      } finally {
        if (mounted) setLoadingWeekRecords(false);
      }
    };

    fetchMonth();

    return () => (mounted = false);
  }, [selectedTimesheetUser, date, status, mode, targetUserIdForApi]);

  // Connect this to the API to allow users to delete records.
  async function deleteRecord(record) {
    setStatus("Deleting...");
    await apiFetch(
      targetUserIdForApi !== null
        ? `/api/timesheets/me?UserID=${targetUserIdForApi}`
        : "/api/timesheets/me",
      {
        method: "DELETE",
        body: {
          UserID: targetUserIdForApi,
          row: record,
        },
      },
    );
    setStatus("Saved");
  }

  async function updateRecord(record) {
    setStatus("Updating...");
    await apiFetch(
      targetUserIdForApi !== null
        ? `/api/timesheets/me?UserID=${targetUserIdForApi}`
        : "/api/timesheets/me",
      {
        method: "PUT",
        body: {
          UserID: targetUserIdForApi,
          row: record,
        },
      },
    );
    setStatus("Saved");
  }

  async function submitSingle(d) {
    const rec = {
      date: formatDateYMD(d),
      hours: Number(hours) || 0,
      project_code: selectedProject ? selectedProject.id : null,
      sub_code: selectedActivity ? selectedActivity.id : null,
      division: (selectedProject && selectedProject.division) || null,
    };
    await apiFetch(
      targetUserIdForApi !== null
        ? `/api/timesheets/me?UserID=${targetUserIdForApi}`
        : "/api/timesheets/me",
      {
        method: "POST",
        body: {
          UserID: targetUserIdForApi,
          records: [rec],
        },
      },
    );
  }

  async function handleSubmit(e) {
    e && e.preventDefault();
    setStatus("Saving...");
    try {
      if (mode === "day") {
        await submitSingle(date);
      } else if (mode === "week") {
        /*const dates = weekHours.filter((d) => {
          console.log(d._hoursOverride)
          return Number(d._hoursOverride) !== 0
        })*/
        const hoursWorked = weekHours.filter((v, i) => {
          return Number(v) !== 0;
        });
        const dates = getWeekDates(date).filter((v, i) => {
          return Number(weekHours[i]) !== 0;
        });
        if (dates.length < 1) {
          setStatus("Please set hours worked.");
          return;
        }
        const records = dates.map((d, index) => ({
          date: formatDateYMD(d),
          hours: hoursWorked[index] || 0,
          project_code: selectedProject ? selectedProject.id : null,
          sub_code: selectedActivity ? selectedActivity.id : null,
          division: (selectedProject && selectedProject.division) || null,
        }));
        await apiFetch(
          targetUserIdForApi !== null
            ? `/api/timesheets/me?UserID=${targetUserIdForApi}`
            : "/api/timesheets/me",
          {
            method: "POST",
            body: {
              UserID: targetUserIdForApi,
              records,
            },
          },
        );
      } else if (mode === "month") {
        const dates = getMonthDates(date).filter((d) => {
          return isWorkingDOTW(selectedTimesheetUser, d.getDay());
        });
        const records = dates.map((d) => ({
          date: formatDateYMD(d),
          hours: isWorkingDOTW(selectedTimesheetUser, d.getDay())
            ? Number(hours)
            : 0 || 0,
          project_code: selectedProject
            ? (selectedProject.id ?? selectedProject.ProjectID)
            : null,
          sub_code: selectedActivity
            ? (selectedActivity.id ?? selectedActivity.ActivityID)
            : null,
          division: (selectedProject && selectedProject.division) || null,
        }));
        console.log(records);
        await apiFetch(
          targetUserIdForApi !== null
            ? `/api/timesheets/me?UserID=${targetUserIdForApi}`
            : "/api/timesheets/me",
          {
            method: "POST",
            body: {
              UserID: targetUserIdForApi,
              records,
            },
          },
        );
      }
      setStatus("Saved");
      setWeekHours(getWeekHours(selectedTimesheetUser, 0));
    } catch (err) {
      console.error(err);
      setStatus(err && err.message ? err.message : String(err));
    }
  }

  const styles = {
    container: { padding: 12 },
    title: { margin: "0 0 12px 0" },
    form: { display: "grid", gap: 12 },
    row: { display: "flex", gap: 12, alignItems: "center" },
    group: { display: "grid", gap: 8 },
    fieldRow: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      flexWrap: "wrap",
    },
    panel: { minWidth: 260, flex: "1 1 260px" },
    weekGrid: { display: "flex", gap: 8, flexWrap: "wrap" },
    smallInput: { width: 120 },
    footer: { display: "flex", gap: 8, alignItems: "center" },
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Timesheets</h3>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.row}>
          {canManageOtherUsers ? (
            <label style={{ display: "grid", gap: 4, minWidth: 260 }}>
              Timesheet user
              <select
                value={selectedUserId || ""}
                onChange={(e) =>
                  setSelectedUserId(Number(e.target.value) || "")
                }
              >
                {users.map((u) => (
                  <option key={u.UserID} value={u.UserID}>
                    {u.UserCode} - {u.UserDescription || "No description"}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            <input
              type="radio"
              checked={mode === "day"}
              onChange={() => setMode("day")}
            />{" "}
            Day
          </label>
          <label>
            <input
              type="radio"
              checked={mode === "week"}
              onChange={() => setMode("week")}
            />{" "}
            Week
          </label>
          <label>
            <input
              type="radio"
              checked={mode === "month"}
              onChange={() => setMode("month")}
            />{" "}
            Month
          </label>
        </div>

        {mode === "day" && (
          <div style={styles.fieldRow}>
            <div style={styles.panel}>
              <label>
                Date
                <div>
                  <DatePicker
                    selected={date}
                    onChange={(d) => setDate(d)}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </label>
            </div>
            <div style={{ ...styles.panel, maxWidth: 160 }}>
              <label>
                Hours
                <input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min={0}
                  max={24}
                  step={0.25}
                  style={styles.smallInput}
                />
              </label>
            </div>
          </div>
        )}

        {mode === "week" && (
          <div style={styles.group}>
            <div style={styles.panel}>
              <label>
                Week (pick any date in the week)
                <div>
                  <DatePicker
                    selected={date}
                    onChange={(d) => setDate(d)}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </label>
            </div>
            <div style={styles.weekGrid}>
              {getWeekDates(date).map((d, date_index) => (
                <div
                  key={d.toISOString()}
                  style={{
                    minWidth: 140,
                    padding: 8,
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                >
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    {d.toDateString()}
                  </div>
                  <input
                    type="number"
                    value={weekHours[date_index]}
                    onChange={(e) => {
                      const newWeekHours = weekHours.map((v, hours_index) =>
                        hours_index === date_index ? e.target.value : v,
                      );
                      setWeekHours(newWeekHours);
                    }}
                    onBlur={(e) => {
                      if (e.target.value !== "") return;
                      const newWeekHours = weekHours.map((v, hours_index) =>
                        hours_index === date_index ? 0 : v,
                      );
                      setWeekHours(newWeekHours);
                    }}
                    min={0}
                    max={24}
                    step={0.25}
                    style={{ width: 100 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {mode === "month" && (
          <div style={styles.fieldRow}>
            <div style={styles.panel}>
              <label>
                Month
                <div>
                  <DatePicker
                    selected={month}
                    onChange={(d) => setMonth(d)}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                  />
                </div>
              </label>
            </div>
            <div style={{ ...styles.panel, maxWidth: 200 }}>
              <label>
                Hours per workday
                <input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min={0}
                  max={24}
                  step={0.25}
                  style={styles.smallInput}
                />
              </label>
            </div>
          </div>
        )}

        <div style={styles.fieldRow}>
          <div style={styles.panel}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              Project (search)
            </div>
            <Typeahead
              items={projects}
              value={selectedProject}
              onChange={(it) => {
                setSelectedProject(it);
              }}
              placeholder="Type project code or description"
              getItemKey={(it) => it.code}
              getItemLabel={(it) => `${it.code} — ${it.desc}`}
              renderItem={(it, q) => (
                <div>
                  <strong style={{ marginRight: 8 }}>{it.code}</strong>
                  <span>{it.desc}</span>
                </div>
              )}
            />
            <div style={{ minHeight: 22, marginTop: 8 }}>
              {selectedProject ? (
                <div>
                  Selected: {selectedProject.code} — {selectedProject.desc}
                </div>
              ) : null}
            </div>
          </div>

          <div style={styles.panel}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              Activity (search)
            </div>
            <Typeahead
              items={activities}
              value={selectedActivity}
              onChange={(it) => setSelectedActivity(it)}
              placeholder="Type activity code or description"
              getItemKey={(it) => it.code}
              getItemLabel={(it) => `${it.code} — ${it.desc}`}
              renderItem={(it) => (
                <div>
                  <strong style={{ marginRight: 8 }}>{it.code}</strong>
                  <span>{it.desc}</span>
                </div>
              )}
            />
            <div style={{ minHeight: 22, marginTop: 8 }}>
              {selectedActivity ? (
                <div>
                  Selected: {selectedActivity.code} — {selectedActivity.desc}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button type="submit">Save</button>
          <div style={{ color: status === "Saved" ? "green" : "darkred" }}>
            {status}
          </div>
        </div>
      </form>
      {/* Weekly breakdown table at the bottom of the Timesheets page */}
      {mode !== "month" ? (
        <div style={{ marginTop: 20 }}>
          <h3>{mode === "day" ? "Daily Breakdown" : "Weekly Breakdown"}</h3>
          <RecordEditOverlay
            record={editModal.record}
            onChange={(v) => deleteRecord(v)}
            onCancel={() => setEditModal({ record: null })}
          />
          <RecordUpdateOverlay
            record={updateModal.record}
            onChange={(v) => updateRecord(v)}
            onCancel={() => setUpdateModal({ record: null })}
          />
          {loadingWeekRecords ? (
            <div>Loading records…</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6 }}>Date</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Entries</th>
                  <th style={{ textAlign: "right", padding: 6 }}>
                    Total Hours
                  </th>
                  <th style={{ textAlign: "right", padding: 6 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(mode === "day" ? [new Date(date)] : getWeekDates(date)).map(
                  (d) => {
                    const key = formatDateYMD(d);
                    const items = (weekRecords || []).filter(
                      (r) => (r.TimesheetDate || "").slice(0, 10) === key,
                    );
                    const sum = items.reduce(
                      (s, it) => s + (Number(it.TimesheetHours) || 0),
                      0,
                    );
                    return (
                      <tr key={key} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: 8 }}>{d.toDateString()}</td>
                        <td style={{ padding: 8 }}>
                          {items.length === 0 ? (
                            <em>No entries</em>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: 14 }}>
                              {items.map((it, i) => (
                                <li
                                  key={key + "_" + i.toString()}
                                  style={{ fontSize: 13, position: "relative" }}
                                >
                                  {it.ProjectCode || "-"} —{" "}
                                  {it.ActivityCode || "-"} —{" "}
                                  {it.TimesheetHours || 0}
                                  {it.TimesheetNotes && (
                                    <span
                                      style={{
                                        color: "#666",
                                        marginLeft: 8,
                                        fontSize: 12,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {it.TimesheetNotes}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: "right" }}>
                          {sum}
                        </td>
                        <td style={{ padding: 8, textAlign: "right" }}>
                          {items.length === 0 ? (
                            <span style={{ color: "#999" }}>-</span>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gap: 4,
                                justifyItems: "end",
                              }}
                            >
                              {items.map((it, i) => (
                                <span
                                  key={key + "_actions_" + i.toString()}
                                  style={{ display: "inline-flex", gap: 6 }}
                                >
                                  <button
                                    type="button"
                                    title="Edit entry"
                                    aria-label="Edit entry"
                                    onClick={() =>
                                      setUpdateModal({ record: it })
                                    }
                                    style={{
                                      border: "1px solid #ddd",
                                      background: "#fff",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                      padding: "0 6px",
                                    }}
                                  >
                                    ✎
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete entry"
                                    aria-label="Delete entry"
                                    onClick={() => setEditModal({ record: it })}
                                    style={{
                                      border: "1px solid #ddd",
                                      background: "#fff",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                      padding: "0 6px",
                                    }}
                                  >
                                    🗑
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  },
                )}
                <tr style={{ fontWeight: 700, borderTop: "2px solid #ddd" }}>
                  <td style={{ padding: 8 }}>
                    {mode === "day" ? "Day Total" : "Week Total"}
                  </td>
                  <td />
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {(weekRecords || []).reduce(
                      (s, it) => s + (Number(it.TimesheetHours) || 0),
                      0,
                    )}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          <h3>Monthly Breakdown</h3>
          <RecordEditOverlay
            record={editModal.record}
            onChange={(v) => deleteRecord(v)}
            onCancel={() => setEditModal({ record: null })}
          />
          <RecordUpdateOverlay
            record={updateModal.record}
            onChange={(v) => updateRecord(v)}
            onCancel={() => setUpdateModal({ record: null })}
          />
          {loadingWeekRecords ? (
            <div>Loading monthly records…</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6 }}>Date</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Entries</th>
                  <th style={{ textAlign: "right", padding: 6 }}>
                    Total Hours
                  </th>
                  <th style={{ textAlign: "right", padding: 6 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getMonthDates(date).map((d) => {
                  const key = formatDateYMD(d);
                  const items = (monthRecords || []).filter(
                    (r) => (r.TimesheetDate || "").slice(0, 10) === key,
                  );
                  const sum = items.reduce(
                    (s, it) => s + (Number(it.TimesheetHours) || 0),
                    0,
                  );
                  return (
                    <tr key={key} style={{ borderTop: "1px solid #eee" }}>
                      <td style={{ padding: 8 }}>{d.toDateString()}</td>
                      <td style={{ padding: 8 }}>
                        {items.length === 0 ? (
                          <em>No entries</em>
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 14 }}>
                            {items.map((it, i) => (
                              <li
                                key={key + "_" + i.toString()}
                                style={{ fontSize: 13, position: "relative" }}
                              >
                                {it.ProjectCode || "-"} —{" "}
                                {it.ActivityCode || "-"} —{" "}
                                {it.TimesheetHours || 0}h
                                {it.TimesheetNotes && (
                                  <span
                                    style={{
                                      color: "#666",
                                      marginLeft: 8,
                                      fontSize: 12,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {it.TimesheetNotes}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>{sum}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        {items.length === 0 ? (
                          <span style={{ color: "#999" }}>-</span>
                        ) : (
                          <div
                            style={{
                              display: "grid",
                              gap: 4,
                              justifyItems: "end",
                            }}
                          >
                            {items.map((it, i) => (
                              <span
                                key={key + "_actions_" + i.toString()}
                                style={{ display: "inline-flex", gap: 6 }}
                              >
                                <button
                                  type="button"
                                  title="Edit entry"
                                  aria-label="Edit entry"
                                  onClick={() => setUpdateModal({ record: it })}
                                  style={{
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    padding: "0 6px",
                                  }}
                                >
                                  ✎
                                </button>
                                <button
                                  type="button"
                                  title="Delete entry"
                                  aria-label="Delete entry"
                                  onClick={() => setEditModal({ record: it })}
                                  style={{
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    padding: "0 6px",
                                  }}
                                >
                                  🗑
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ fontWeight: 700, borderTop: "2px solid #ddd" }}>
                  <td style={{ padding: 8 }}>Month Total</td>
                  <td />
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {(monthRecords || []).reduce(
                      (s, it) => s + (Number(it.TimesheetHours) || 0),
                      0,
                    )}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
